import React, { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon, InformationCircleIcon, QuestionMarkCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const icons = {
    danger: <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />,
    warning: <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />,
    info: <InformationCircleIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />,
    success: <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />,
    question: <QuestionMarkCircleIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />,
};

const bgColors = {
    danger: 'bg-red-100',
    warning: 'bg-yellow-100',
    info: 'bg-blue-100',
    success: 'bg-green-100',
    question: 'bg-indigo-100',
};

const buttonColors = {
    danger: 'bg-red-600 hover:bg-red-500 focus-visible:outline-red-600',
    warning: 'bg-yellow-600 hover:bg-yellow-500 focus-visible:outline-yellow-600',
    info: 'bg-blue-600 hover:bg-blue-500 focus-visible:outline-blue-600',
    success: 'bg-green-600 hover:bg-green-500 focus-visible:outline-green-600',
    question: 'bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600',
};

export default function ConfirmDialog({ isOpen, onClose, options }) {
    const cancelButtonRef = useRef(null);
    const { 
        title = 'Confirmação', 
        message = 'Tem certeza?', 
        type = 'question', 
        confirmText = 'Confirmar', 
        cancelText = 'Cancelar',
        onConfirm 
    } = options || {};

    const handleConfirm = () => {
        if (onConfirm) onConfirm();
        onClose();
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[110]" initialFocus={cancelButtonRef} onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                <div className="sm:flex sm:items-start">
                                    <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${bgColors[type] || bgColors.question} sm:mx-0 sm:h-10 sm:w-10`}>
                                        {icons[type] || icons.question}
                                    </div>
                                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                        <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                            {title}
                                        </Dialog.Title>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                {message}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                    <button
                                        type="button"
                                        className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto ${buttonColors[type] || buttonColors.question}`}
                                        onClick={handleConfirm}
                                    >
                                        {confirmText}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                        onClick={onClose}
                                        ref={cancelButtonRef}
                                    >
                                        {cancelText}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
